import { useSubscription as useApolloSubscription } from "@apollo/client";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/fr";
import { get } from "lodash";
import { formatNotificationDateAsFromNow } from "utils/helpers/notificationDate";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  MdClose,
  MdNotificationsActive,
  MdOutlineChevronRight,
  MdPersonAdd,
  MdPhoneMissed,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import CallButton from "shared/components/CallButton";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import { NOTIFICATION_TYPES } from "utils/constants";
import {
  canCallFromNotification,
  getNotificationAction,
  getNotificationActionLabelKey,
} from "utils/helpers/notificationActions";

dayjs.extend(relativeTime);
dayjs.extend(utc);

const BANNER_DURATION = 8000;

const BANNER_CONFIG = {
  [NOTIFICATION_TYPES.TASK_REMINDER]: {
    icon: MdNotificationsActive,
    colorClass: "notification-banner--task",
    actionLabel: "VIEW_TASK",
  },
  [NOTIFICATION_TYPES.MISSED_CALL]: {
    icon: MdPhoneMissed,
    colorClass: "notification-banner--call",
    actionLabel: "VIEW_CONTACT",
  },
  [NOTIFICATION_TYPES.NEW_LEAD]: {
    icon: MdPersonAdd,
    colorClass: "notification-banner--lead",
    actionLabel: "VIEW_PROJECT",
  },
  [NOTIFICATION_TYPES.SYSTEM]: {
    icon: MdNotificationsActive,
    colorClass: "notification-banner--task",
    actionLabel: "VIEW_SUBSCRIPTION",
  },
};

const NotificationBannerContext = createContext({});

const BannerItem = ({ banner, onDismiss, onAction, hasAction, canCall }) => {
  const { t, i18n } = useTranslation(NAME_SPACES.PRIVATE.BROKER.NOTIFICATION);
  const LIST = t("LIST", { returnObjects: true });
  const language = i18n.language || "fr";
  const config =
    BANNER_CONFIG[banner.type] ||
    BANNER_CONFIG[NOTIFICATION_TYPES.TASK_REMINDER];
  const actionLabel =
    getNotificationActionLabelKey(banner) || config.actionLabel;
  const IconComponent = config.icon;

  return (
    <div
      className={classNames("notification-banner", config.colorClass, {
        "notification-banner--entering": banner.entering,
      })}
    >
      <div className="notification-banner__body">
        <div className="notification-banner__icon">
          <IconComponent size={22} />
        </div>
        <div className="notification-banner__content">
          <div className="notification-banner__title">{banner.title}</div>
          <p className="notification-banner__message">{banner.content}</p>
          <span className="notification-banner__date">
            {formatNotificationDateAsFromNow(banner.createdDate, language)}
          </span>
        </div>
        <button
          className="notification-banner__close"
          onClick={() => onDismiss(banner.id)}
        >
          <MdClose size={18} />
        </button>
      </div>
      <div className="notification-banner__actions">
        {canCall && (
          <CallButton
            phoneNumber={get(banner, "meta.phoneNumber")}
            contactId={get(banner, "meta.contactId")}
            projectId={get(banner, "meta.projectId")}
            buttonClassName="notification-banner__call-btn"
            getPopupContainer={(trigger) =>
              trigger?.closest(".notification-banner-container") ||
              document.body
            }
          />
        )}
        {hasAction && (
          <button
            className="notification-banner__action-btn notification-banner__action-btn--primary"
            onClick={() => onAction(banner)}
          >
            {LIST[actionLabel]}
            <MdOutlineChevronRight size={16} />
          </button>
        )}
        <button
          className="notification-banner__action-btn notification-banner__action-btn--secondary"
          onClick={() => onDismiss(banner.id)}
        >
          {LIST.DISMISS || "Ignorer"}
        </button>
      </div>
      <div className="notification-banner__progress">
        <div
          className="notification-banner__progress-bar"
          style={{ animationDuration: `${BANNER_DURATION}ms` }}
        />
      </div>
    </div>
  );
};

const NotificationBannerProvider = ({ children }) => {
  const { user } = useContext(StoreContext);
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const timersRef = useRef({});
  const shownBannerIdsRef = useRef(new Set());

  const removeBanner = useCallback((bannerId) => {
    if (timersRef.current[bannerId]) {
      clearTimeout(timersRef.current[bannerId]);
      delete timersRef.current[bannerId];
    }
    setBanners((prev) => prev.filter((b) => b.id !== bannerId));
  }, []);

  const [markAsRead] = useMutation(graphql.mutations.UPDATE_NOTIFICATION, {
    refetchQueries: ["UnreadNotificationsCount"],
  });

  const handleAction = useCallback(
    (banner) => {
      markAsRead({
        variables: {
          where: { id: banner.id },
          data: {
            isReadBy: [{ id: user?.id }],
          },
        },
      });
      removeBanner(banner.id);

      const action = getNotificationAction(banner);
      if (action) {
        action(banner, { navigate });
      }
    },
    [user?.id, navigate, markAsRead, removeBanner],
  );

  const pushBanner = useCallback(
    (notification) => {
      if (shownBannerIdsRef.current.has(notification.id)) return;
      shownBannerIdsRef.current.add(notification.id);

      const newBanner = { ...notification, entering: true };
      setBanners((prev) => [newBanner, ...prev]);

      setTimeout(() => {
        setBanners((prev) =>
          prev.map((b) =>
            b.id === notification.id ? { ...b, entering: false } : b,
          ),
        );
      }, 50);

      timersRef.current[notification.id] = setTimeout(() => {
        removeBanner(notification.id);
      }, BANNER_DURATION);
    },
    [removeBanner],
  );

  const addBannerIfNew = useCallback(
    (notification) => {
      if (!notification?.id || !user?.id) return;
      if (shownBannerIdsRef.current.has(notification.id)) return;
      const created = dayjs.utc(
        typeof notification.createdDate === "string" &&
          !notification.createdDate.endsWith("Z")
          ? `${notification.createdDate}Z`
          : notification.createdDate,
      );
      if (
        !created.isValid() ||
        created.isBefore(dayjs.utc().subtract(2, "minute"))
      )
        return;
      pushBanner(notification);
    },
    [user?.id, pushBanner],
  );

  useApolloSubscription(graphql.subscriptions.NOTIFICATION_ADDED, {
    skip: !user,
    onData: ({ data: subscriptionData }) => {
      const notification = get(subscriptionData, "data.notificationAdded");
      if (!notification) return;

      const recipients = notification.recipients || [];
      const isRecipient =
        recipients.length > 0
          ? recipients.some((r) => String(r.id) === String(user?.id))
          : true;
      if (!isRecipient) return;

      pushBanner(notification);
    },
  });

  useEffect(() => {
    const currentTimers = timersRef.current;
    return () => {
      Object.values(currentTimers).forEach(clearTimeout);
    };
  }, []);

  return (
    <NotificationBannerContext.Provider value={{ addBannerIfNew }}>
      {children}
      {banners.length > 0 && (
        <div className="notification-banner-container">
          {banners.map((banner) => (
            <BannerItem
              key={banner.id}
              banner={banner}
              onDismiss={removeBanner}
              onAction={handleAction}
              hasAction={Boolean(getNotificationAction(banner))}
              canCall={canCallFromNotification(banner, user)}
            />
          ))}
        </div>
      )}
    </NotificationBannerContext.Provider>
  );
};

export const useNotificationBanner = () =>
  useContext(NotificationBannerContext);

export default NotificationBannerProvider;
